import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, TreePine, Factory, Truck } from "lucide-react";
import { getProducts } from "@/lib/api";
import { IMG, DIVISIONS, PRODUCT_TYPES, CARE_CARDS, Swoosh } from "@/data/site";
import { Reveal, MaskedLine, Kicker, EASE } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import QuoteCTA from "@/components/QuoteCTA";

const SUPPLY_STEPS = [
  {
    icon: TreePine,
    title: "Manufactured in PNG",
    copy: "Sawmilling, kiln drying, joinery and furniture manufacturing within our Papua New Guinea group operations.",
  },
  {
    icon: Factory,
    title: "Exported to Brisbane",
    copy: "The PNG export division supplies our Australian division, warehoused at Archerfield, Brisbane.",
  },
  {
    icon: Truck,
    title: "Delivered to you",
    copy: "Retail orders, trade supply and project freight across Queensland and Australia — local pickup available.",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  useEffect(() => {
    getProducts({ featured: "true" }).then((r) => setFeatured(r.slice(0, 4)));
  }, []);

  return (
    <div data-testid="home-page">
      {/* DIVISIONS */}
      <section className="px-5 pb-16 pt-32 md:px-10 md:pb-20 md:pt-40" data-testid="divisions-section">
        <div className="mx-auto max-w-[1500px]">
          <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <div>
              <Kicker>One Brand</Kicker>
              <h2 className="mt-3 font-display text-4xl font-light tracking-tight md:text-6xl">
                Three divisions.<br />One standard of timber.
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-ink/60">
              Retail-ready outdoor furniture, trade timber supply and solid timber doors — each with its own
              range, expertise and quote pathway.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
            {DIVISIONS.map((d, i) => (
              <Reveal
                key={d.key}
                delay={i * 0.1}
                className={i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : "md:col-span-12"}
              >
                <Link
                  to={d.path}
                  data-testid={`division-card-${d.key}`}
                  className="group relative block aspect-[4/3] overflow-hidden rounded-sm bg-ink md:aspect-auto md:h-[460px]"
                >
                  <img
                    src={d.image}
                    alt={`${d.name} — ${d.tag}`}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" />
                  <span className="absolute left-6 top-5 font-display text-6xl font-light text-outline-cream md:text-7xl">
                    {d.num}
                  </span>
                  <div className="absolute inset-x-6 bottom-6 flex items-end justify-between gap-4">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-cream/60">{d.tag}</p>
                      <h3 className="mt-1 font-display text-4xl text-cream md:text-5xl">{d.name}</h3>
                      <p className="mt-2 max-w-md text-sm text-cream/70">{d.copy}</p>
                    </div>
                    <span className="flex h-12 w-12 shrink-0 translate-y-2 items-center justify-center rounded-full bg-cream text-ink opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                      <ArrowUpRight size={18} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden px-5 pb-20 pt-10 md:px-10 md:pt-14 lg:min-h-[80vh]">
        <div className="mx-auto grid max-w-[1500px] items-end gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.8 }}
              className="text-[11px] font-bold uppercase tracking-[0.3em] text-kwila"
              data-testid="hero-kicker"
            >
              Premium Kwila & Tropical Hardwood — Est. Papua New Guinea
            </motion.p>
            <h1 className="mt-6 font-display font-light leading-[0.88] tracking-tight" data-testid="hero-headline">
              <MaskedLine delay={0.2} className="text-[19vw] sm:text-[15vw] lg:text-[8.5rem]">MR ~</MaskedLine>
              <MaskedLine delay={0.32} className="text-[19vw] sm:text-[15vw] lg:text-[8.5rem]">KWILA</MaskedLine>
            </h1>
            <motion.div
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{ delay: 0.9, duration: 1, ease: EASE }}
              className="mt-2 w-3/4 origin-left md:w-1/2"
            >
              <Swoosh />
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.8, ease: EASE }}
              className="mt-6 text-xs font-bold uppercase tracking-[0.42em] text-ink/70"
              data-testid="hero-divisions-line"
            >
              Timber &nbsp;|&nbsp; Doors &nbsp;|&nbsp; Furniture
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
              className="mt-5 max-w-lg text-base leading-relaxed text-ink/70"
            >
              Premium Kwila and tropical hardwood products, manufactured in Papua New Guinea and distributed
              from Brisbane. Shop outdoor furniture, source timber supply, or request a quote for solid timber
              doors, decking and project products.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.8, ease: EASE }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link to="/shop?division=furniture" data-testid="hero-shop-furniture-button" className="flex items-center gap-2 rounded-full bg-ink px-7 py-3 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-colors hover:bg-kwila">
                Shop Furniture <ArrowRight size={14} />
              </Link>
              <Link to="/timber" data-testid="hero-explore-timber-button" className="flex items-center gap-2 rounded-full border border-ink/30 px-7 py-3 text-xs font-bold uppercase tracking-[0.14em] transition-colors hover:bg-ink hover:text-cream">
                Explore Timber
              </Link>
              <Link to="/doors" data-testid="hero-view-doors-button" className="flex items-center gap-2 rounded-full border border-ink/30 px-7 py-3 text-xs font-bold uppercase tracking-[0.14em] transition-colors hover:bg-ink hover:text-cream">
                View Doors
              </Link>
              <Link to="/request-quote" data-testid="hero-request-quote-button" className="flex items-center gap-2 rounded-full bg-kwila px-7 py-3 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-colors hover:bg-ink">
                Request a Quote <ArrowUpRight size={14} />
              </Link>
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <motion.div style={{ y: imgY }} className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.45, duration: 1.1, ease: EASE }}
                className="overflow-hidden rounded-t-[200px]"
              >
                <motion.img
                  src="/hero-deck.png"
                  alt="Vitex (Teak) decking around a pool with hardwood-clad Australian home at dusk"
                  initial={{ scale: 1.18 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 1.8, ease: EASE }}
                  className="aspect-[3/4] w-full object-cover"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.8, ease: EASE }}
                className="absolute -bottom-6 -left-6 max-w-[240px] rounded-sm bg-ink p-5 text-cream shadow-xl md:-left-10"
              >
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-kwila">From forest to floor</p>
                <p className="mt-2 font-display text-lg leading-snug">
                  PNG manufactured. Brisbane distributed. Australian delivered.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* FEATURED PRODUCTS */}
      <section className="bg-stone/60 px-5 py-24 md:px-10 md:py-32" data-testid="featured-section">
        <div className="mx-auto max-w-[1500px]">
          <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <div>
              <Kicker>The Range</Kicker>
              <h2 className="mt-3 font-display text-4xl font-light tracking-tight md:text-6xl">Featured pieces</h2>
            </div>
            <Link
              to="/shop"
              data-testid="featured-view-all-link"
              className="flex items-center gap-2 rounded-full border border-ink/30 px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] transition-colors hover:bg-ink hover:text-cream"
            >
              View Full Range <ArrowRight size={14} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* WHAT IS KWILA */}
      <section className="px-5 py-24 md:px-10 md:py-32" data-testid="what-is-kwila-section">
        <div className="mx-auto grid max-w-[1500px] items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="overflow-hidden rounded-t-[160px]">
              <img
                src="/kwila-panelling.png"
                alt="Designer hardwood timber panelling feature wall"
                loading="lazy"
                className="aspect-[4/5] w-full object-cover transition-transform duration-[900ms] hover:scale-105"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-7 lg:pl-8">
            <Reveal>
              <Kicker>The Timber</Kicker>
              <h2 className="mt-3 font-display text-4xl font-light tracking-tight md:text-6xl">What is Kwila?</h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/70 md:text-lg">
                Kwila, also known as Merbau, is a premium tropical hardwood known for its rich colour, density,
                durability and resistance to outdoor conditions. It is commonly used for outdoor furniture,
                decking, doors, flooring, joinery and heavy-use timber applications.
              </p>
              <div className="mt-8 grid max-w-xl grid-cols-1 gap-px overflow-hidden rounded-sm border border-ink/10 bg-ink/10 sm:grid-cols-3">
                {[
                  ["Species", "Intsia bijuga"],
                  ["Also known as", "Merbau"],
                  ["Character", "Dense & durable"],
                ].map(([k, v]) => (
                  <div key={k} className="bg-cream p-5">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-sand">{k}</p>
                    <p className="mt-1 font-display text-xl italic">{v}</p>
                  </div>
                ))}
              </div>
              <Link
                to="/kwila-merbau"
                data-testid="learn-about-kwila-link"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-colors hover:bg-kwila"
              >
                Learn About Kwila <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SUPPLY CHAIN */}
      <section className="bg-ink px-5 py-24 text-cream md:px-10 md:py-32" data-testid="supply-chain-section">
        <div className="mx-auto max-w-[1500px]">
          <Reveal className="mb-14 max-w-3xl">
            <Kicker dark>Our Supply Chain</Kicker>
            <h2 className="mt-3 font-display text-4xl font-light leading-tight tracking-tight md:text-6xl">
              From Papua New Guinea to your project.
            </h2>
          </Reveal>
          <div className="grid gap-10 md:grid-cols-3">
            {SUPPLY_STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.12} className="border-t border-cream/15 pt-8">
                <div className="flex items-center justify-between">
                  <s.icon size={28} className="text-kwila" strokeWidth={1.5} />
                  <span className="font-display text-5xl font-light text-outline-cream">0{i + 1}</span>
                </div>
                <h3 className="mt-6 font-display text-2xl md:text-3xl">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-cream/60">{s.copy}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/about"
              data-testid="supply-chain-about-link"
              className="mt-12 inline-flex items-center gap-2 rounded-full border border-cream/30 px-7 py-3 text-xs font-bold uppercase tracking-[0.14em] transition-colors hover:bg-cream hover:text-ink"
            >
              Our Story <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SHOP BY PRODUCT TYPE */}
      <section className="px-5 py-24 md:px-10 md:py-32" data-testid="shop-by-type-section">
        <div className="mx-auto max-w-[1500px]">
          <Reveal className="mb-10">
            <Kicker>Browse</Kicker>
            <h2 className="mt-3 font-display text-4xl font-light tracking-tight md:text-6xl">Shop by product type</h2>
          </Reveal>
          <div className="border-t border-ink/10">
            {PRODUCT_TYPES.map((t, i) => (
              <Reveal key={t.label} delay={i * 0.05}>
                <Link
                  to={t.to}
                  data-testid={`product-type-${t.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group flex items-center justify-between gap-6 border-b border-ink/10 py-6 transition-colors hover:bg-stone/50 md:py-8"
                >
                  <div className="flex items-baseline gap-5">
                    <span className="text-xs font-bold text-kwila">0{i + 1}</span>
                    <span className="font-display text-3xl tracking-tight transition-transform duration-300 group-hover:translate-x-2 md:text-5xl">
                      {t.label}
                    </span>
                  </div>
                  <ArrowUpRight size={22} className="shrink-0 text-sand transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-kwila" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRADE BAND */}
      <section className="bg-ink px-5 py-20 text-cream md:px-10 md:py-28" data-testid="trade-band-section">
        <div className="mx-auto flex max-w-[1500px] flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
          <Reveal>
            <Kicker dark>Trade & Reseller</Kicker>
            <h2 className="mt-3 max-w-2xl font-display text-4xl font-light leading-tight tracking-tight md:text-5xl">
              Builders, joiners, merchants, door manufacturers & specifiers.
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-cream/70 md:text-base">
              Apply for a trade account for priority pricing on timber packs, door components and project
              supply — or send a bulk quote request with your cutting list.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="flex flex-wrap gap-3">
            <Link to="/trade" data-testid="trade-apply-button" className="flex items-center gap-2 rounded-full bg-cream px-7 py-3 text-xs font-bold uppercase tracking-[0.14em] text-ink transition-colors hover:bg-ink hover:text-cream">
              Apply for Trade Account <ArrowUpRight size={14} />
            </Link>
            <Link to="/request-quote" data-testid="trade-quote-button" className="flex items-center gap-2 rounded-full border border-cream/40 px-7 py-3 text-xs font-bold uppercase tracking-[0.14em] transition-colors hover:bg-cream hover:text-ink">
              Trade Quote
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CARE */}
      <section className="px-5 py-24 md:px-10 md:py-32" data-testid="care-section">
        <div className="mx-auto max-w-[1500px]">
          <Reveal className="mb-12">
            <Kicker>Looking After Kwila</Kicker>
            <h2 className="mt-3 font-display text-4xl font-light tracking-tight md:text-6xl">Care & maintenance</h2>
          </Reveal>
          <div className="grid gap-8 md:grid-cols-3">
            {CARE_CARDS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.1}>
                <Link to={c.to} data-testid={`care-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group block">
                  <div className="aspect-[4/3] overflow-hidden rounded-sm">
                    <img src={c.image} alt={c.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <h3 className="mt-4 font-display text-2xl tracking-tight">{c.title}</h3>
                  <p className="mt-1 text-sm text-ink/60">{c.copy}</p>
                  <span className="mt-3 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-kwila">
                    Read the guide <ArrowRight size={13} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <QuoteCTA />
    </div>
  );
}
